import * as React from 'react';
import * as moment from 'moment';
import {decode} from 'html-entities';
import CardList, {ListItem} from '../../shared/CardList';
import {OrderType, SkuVariety, WaveParameters} from '../ecommerce-batches/BatchInterfaces';
import SimpleKebabMenu from '../../shared/SimpleKebabMenu';
import WaveTemplateServiceV2 from '../../shared/services/WaveTemplateServiceV2';
import {WaveTemplate} from './wave-templates/WaveTemplateInterfaces';

interface WaveTemplateListProps {
  isPastDue: boolean;
  waveTemplates: WaveTemplate[];
  waveTemplateServiceV2: WaveTemplateServiceV2;
  reservationToSortationEnabled: object;
  reservationIdToName: object;
  carriers: object;
  loadForWarehouse();
  setShowWaveModal(show: boolean);
  setTemplateToWave(template: WaveTemplate);
  errorCatapult(errors: string[]);
}

const waveButtonText = 'Wave';
const waveTemplateFormRoute = '/wh/waves/templates/new';

const WaveTemplateList: React.FC<WaveTemplateListProps> = (props) => {
  const {
    isPastDue,
    waveTemplates,
    waveTemplateServiceV2,
    reservationToSortationEnabled,
    reservationIdToName,
    carriers,
    loadForWarehouse,
    setShowWaveModal,
    setTemplateToWave,
    errorCatapult
  } = props;

  const formatWaveFilters = (waveFilters: WaveParameters) => {
    const details = [];

    if (isPastDue && waveFilters.cutoffDate) {
      details.push(`Ship by: ${moment.utc(waveFilters.cutoffDate).format('MMM DD')}`);
    }

    const reservationName = reservationIdToName[waveFilters.reservationId];
    if (reservationName) {
      details.push(reservationName);
    } else {
      details.push(`Reservation: ${waveFilters.reservationId}`);
    }

    if (reservationToSortationEnabled[waveFilters.reservationId]) {
      details.push('Rebin');
    }

    if (waveFilters.purchaseOrder) {
      details.push(`PO: ${waveFilters.purchaseOrder}`);
    }

    if (waveFilters.carriers) {
      const displayable = waveFilters.carriers.map((carrierEnum) => carriers[carrierEnum]);
      details.push(displayable.join(', '));
    }
    if (waveFilters.serviceTypes && waveFilters.serviceTypes.length > 0) {
      details.push(waveFilters.serviceTypes.join(', '));
    }
    if (waveFilters.shipmentType === OrderType.SingleItem) {
      details.push('Single Unit');
    } else if (waveFilters.shipmentType === OrderType.MultiItem) {
      details.push('Multi Unit');
    } // else ignore all other values
    if (waveFilters.skuVariety === SkuVariety.SingleSku) {
      details.push('Single SKU');
    } else if (waveFilters.skuVariety === SkuVariety.MultiSku) {
      details.push('Multi SKU');
    } // else ignore all other values

    if (waveFilters.shipAsIs) {
      details.push('Ship As Is');
    }
    if (waveFilters.siteToStoreOnly) {
      details.push('Site to Store');
    }
    if (waveFilters.includesHazmat) {
      details.push('Hazmat');
    }
    if (waveFilters.skus && waveFilters.skus.length > 0) {
      details.push(
        `SKU: ${waveFilters.skus
          .map((sku) => {
            return sku.replace('&amp;', '&');
          })
          .join(', ')}`
      );
    }
    if (waveFilters.postalCodeRange) {
      const range = waveFilters.postalCodeRange;
      details.push(`Postal Codes: ${range.start} - ${range.end}`);
    }

    if (waveFilters.customAttributes) {
      for (const [k, v] of Object.entries(waveFilters.customAttributes)) {
        details.push(`${k}: ${v}`);
      }
    }

    return details.join(', ');
  };

  const deleteTemplate = async (id: number) => {
    const response = await waveTemplateServiceV2.deleteWaveTemplate(id);
    // Server returns a 204 which is modeled by an empty response object so check for errors
    if (response?.errors?.length > 0) {
      errorCatapult(['An error occurred while deleting this template']);
    } else {
      loadForWarehouse();
    }
  };

  const getCallBackForTemplate = (template: WaveTemplate) => {
    return () => {
      setShowWaveModal(true);
      setTemplateToWave(template);
    };
  };

  const configureListItems = (templates: WaveTemplate[]): ListItem[] => {
    return templates.map((template) => {
      // \u00A0 - This is the unicode for a non-breaking space. &nbsp; does not work.
      const shipments = `${template.count}\u00A0shipments`;
      const templateName = decode(template.templateName);
      const options = [
        {
          optionText: 'Edit',
          optionAction: () => window.location.assign(`/wh/waves/templates/${template.templateId}/edit`)
        },
        {
          optionText: 'Duplicate',
          optionAction: () => window.location.assign(`${waveTemplateFormRoute}?id=${template.templateId}`)
        }
      ];

      return {
        id: template.templateId,
        title: templateName,
        info: formatWaveFilters(template.waveFilters),
        actionHint: shipments,
        buttonText: waveButtonText,
        actionClick: getCallBackForTemplate(template),
        needsAttention: isPastDue,
        actionClickDisabled: !template.count,
        optionMenu: isPastDue ? null : (
          <SimpleKebabMenu
            title={templateName}
            id={template.templateId}
            options={options}
            deleteFunction={deleteTemplate}
            deleteEnabled
          />
        )
      };
    });
  };

  const title = isPastDue ? 'Past Due' : 'Wave by Template';
  return (
    <React.Fragment>
      <CardList
        listTitle={title}
        listItems={configureListItems(waveTemplates)}
        createRoute={isPastDue ? null : waveTemplateFormRoute}
        emptyListText={isPastDue ? '' : 'Click "Create New" to create new templates.'}
      />
    </React.Fragment>
  );
};

export default WaveTemplateList;
